import { useAuthStore } from '@/stores/useAuthStore';
import { useMyPersonEntityId } from './useMyPersonEntityId';

/**
 * Pure function that resolves role conflicts.
 *
 * Precedence (most restrictive wins): viewer > frontwindow > user/admin.
 * All accounts carry the `user` role.  Business rules:
 *   frontwindow + user            → Front Window only, NO conflict
 *   frontwindow + user + admin    → Front Window only, conflict
 *   frontwindow + user + viewer   → Viewer only, conflict
 *   fw + user + viewer + admin    → Viewer only, conflict
 */
export const resolveRoles = (roles: string[]) => {
  const hasViewer = roles.includes('viewer');
  const hasFrontWindow = roles.includes('frontwindow');
  // Admin supersedes service-member: an admin who incidentally carries the
  // service-member role (e.g. via demo role auto-assignment in TEST) must not
  // have Issue Out / Turn-In / Exchange buttons hidden. Suppressed when viewer
  // wins (viewer already suppresses everything) or frontwindow wins.
  const hasAdmin = roles.includes('admin') && !hasViewer;

  // Viewer overrides everything; frontwindow overrides user/admin.
  const isFrontWindowOnly = hasFrontWindow && !hasViewer;
  const hasConflict =
    (hasViewer && roles.some((r) => r === 'admin' || r === 'frontwindow')) ||
    (isFrontWindowOnly && roles.some((r) => r === 'admin'));

  const isEffectiveRole = (role: string): boolean => {
    if (!roles.includes(role)) return false;
    if (hasViewer && role !== 'viewer') return false;
    if (isFrontWindowOnly && role !== 'frontwindow') return false;
    // Admin supersedes service-member — hide service-member UI gates for admins.
    if (hasAdmin && !isFrontWindowOnly && role === 'service-member') return false;
    return true;
  };

  return {
    isViewer: hasViewer,
    isFrontWindowOnly,
    hasConflict,
    isEffectiveRole,
  };
};

export const useAuth = () => {
  const {
    user,
    accessToken,
    idToken,
    refreshToken,
    isAuthenticated,
    isLoading,
    isDemoServiceMemberSession,
    login,
    loginWithHint,
    loginAsDemoServiceMember,
    clearAuth,
    logout,
    refreshTokens,
    setTokens,
  } = useAuthStore();

  const userRoles = user?.roles ?? [];
  const { isViewer, isFrontWindowOnly, hasConflict, isEffectiveRole } = resolveRoles(userRoles);

  // isLinkedPerson is true whenever the account is linked to a person entity,
  // regardless of effective role. This enables additive 'My CIF' access for
  // operational users (CIF/HR/Comptroller/Finance) who are also linked persons.
  const { myPersonEntityId, isLoading: isLinkedPersonLoading } = useMyPersonEntityId();
  const isLinkedPerson = isAuthenticated && myPersonEntityId !== null;

  return {
    // State
    user,
    accessToken,
    idToken,
    refreshToken,
    isAuthenticated,
    isLoading,
    isDemoServiceMemberSession,

    // Actions
    login,
    loginWithHint,
    loginAsDemoServiceMember,
    clearAuth,
    logout,
    refreshTokens,
    setTokens,

    // Computed — viewer wins when conflict exists; frontwindow wins over user/admin
    hasRole: isEffectiveRole,
    hasAnyRole: (roles: string[]) => roles.some(isEffectiveRole),
    isAdmin: isEffectiveRole('admin'),
    isUser: isEffectiveRole('user'),
    isViewer,
    isFrontWindowOnly,
    hasRoleConflict: hasConflict,
    /** True when the authenticated user carries the service-member role and is
     *  not overridden by a higher-precedence role (viewer wins). */
    isServiceMember: isEffectiveRole('service-member'),
    /** True when the authenticated user carries the comptroller or finance role.
     *  Both roles share the same permissions: can view people, Issue Out, Exchange,
     *  and restricted (buy) program; cannot edit person/sizing, Turn-In, access Buy Program,
     *  or manage users.
     *  Checked against the raw role set (not isEffectiveRole) because these accounts
     *  also carry 'frontwindow' to gain Front Window access, and frontwindow's
     *  precedence over other roles would otherwise mask this flag and defeat the
     *  restricted-program-scoping restriction (#1085). */
    isComptroller: userRoles.includes('comptroller') || userRoles.includes('finance'),
    /** True when the authenticated user carries the hr role.
     *  Checked against the raw role set for the same reason as isComptroller above. */
    isHr: userRoles.includes('hr'),
    /**
     * True whenever the current account is linked to a person entity,
     * independent of the effective role. A CIF/HR/Comptroller/Finance user whose
     * account is linked to a person entity has this set to true even though
     * `isServiceMember` is false (operational role wins precedence).
     * Used to gate additive 'My CIF' self-service views.
     */
    isLinkedPerson,
    isLinkedPersonLoading,
  };
};
